import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, GitBranch, Plus, Trash2, User } from "lucide-react";

import { CrmRecordAutocomplete, type CrmPickerEntity } from "@/components/ui/crm-record-autocomplete";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { invalidateDealViews } from "@/lib/queryKeys";
import AssociationDecisionDialog from "@/components/associations/AssociationDecisionDialog";

type ParticipantType = Extract<CrmPickerEntity, "contact" | "company" | "branch">;

interface DealParticipantRow {
  id: number;
  entityType: ParticipantType;
  entityId: number;
  entityName?: string | null;
  entityCompanyName?: string | null;
}

interface PrimaryDealRelation { id: number; name: string; companyName?: string | null }
interface SuggestedContact {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  companyEmail?: string | null;
}
interface SuggestedContactGroup {
  relationship: "direct" | "controlling_company" | "parent_company";
  name?: string;
  companyName?: string;
  contacts: SuggestedContact[];
}
interface SuggestedContactsResponse { groups: SuggestedContactGroup[] }
type AssociationDecisionTarget = "deal_participant" | "deal_primary_contact" | "deal_primary_company";

export function DealParticipantsPanel({
  dealId,
  createRequestKey = 0,
  primaryCompany,
  primaryContact,
}: {
  dealId: number;
  createRequestKey?: number;
  primaryCompany?: PrimaryDealRelation | null;
  primaryContact?: PrimaryDealRelation | null;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [entityType, setEntityType] = React.useState<ParticipantType>("contact");
  const [entityId, setEntityId] = React.useState("");
  const [entityLabel, setEntityLabel] = React.useState("");
  const [suggestedContactIds, setSuggestedContactIds] = React.useState<number[]>([]);
  const [associationDecision, setAssociationDecision] = React.useState<{ target: AssociationDecisionTarget; associationId: number; label: string } | null>(null);
  const queryKey = [`/api/deals/${dealId}/participants`];
  const { data: participants = [] } = useQuery<DealParticipantRow[]>({ queryKey });
  const suggestedContactsEndpoint = entityType === "company"
    ? `/api/companies/${entityId}/contact-groups`
    : `/api/branches/${entityId}/contact-groups`;
  const { data: suggestedContactGroups, isFetching: isFetchingSuggestions } = useQuery<SuggestedContactsResponse>({
    queryKey: [suggestedContactsEndpoint],
    enabled: Boolean(entityId) && entityType !== "contact",
  });
  const linkedContactIds = React.useMemo(() => new Set([
    ...participants.filter((participant) => participant.entityType === "contact").map((participant) => participant.entityId),
    ...(primaryContact ? [primaryContact.id] : []),
  ]), [participants, primaryContact]);
  const displayedParticipants = React.useMemo(() => {
    const contactCompanyNames = new Set(participants.filter((item) => item.entityType === "contact" && item.entityCompanyName).map((item) => item.entityCompanyName));
    return participants.filter((item) => item.entityType !== "company" || !contactCompanyNames.has(item.entityName));
  }, [participants]);

  React.useEffect(() => {
    if (createRequestKey > 0) setOpen(true);
  }, [createRequestKey]);

  const addParticipant = useMutation({
    mutationFn: () => apiRequest("POST", `/api/deals/${dealId}/participants`, {
      entityType,
      entityId: Number(entityId),
      suggestedContactIds,
    }),
    onSuccess: async () => {
      invalidateDealViews(queryClient, dealId, ["participants"]);
      setEntityId("");
      setEntityLabel("");
      setSuggestedContactIds([]);
      setOpen(false);
    },
  });

  const typeIcon = (type: ParticipantType) => type === "contact"
    ? <User className="h-4 w-4" />
    : type === "company" ? <Building2 className="h-4 w-4" /> : <GitBranch className="h-4 w-4" />;

  return (
    <>
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setOpen(true)} className="rounded-none">
            <Plus className="mr-1.5 h-4 w-4" /> Aggiungi persona o azienda
          </Button>
        </div>
        {!primaryCompany && !primaryContact && displayedParticipants.length === 0 ? (
          <div className="border border-border bg-background px-4 py-6 text-center text-sm text-muted-foreground">
            Nessun partecipante aggiuntivo collegato.
          </div>
        ) : (
          <div className="border border-border bg-background">
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] border-b border-border bg-muted/30 px-3 py-2 text-xs font-semibold uppercase text-muted-foreground">
              <span>Azienda</span><span>Partecipante</span><span />
            </div>
            {primaryCompany || primaryContact ? (
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center border-b border-border px-3 py-2 text-sm">
                <span className="truncate font-medium">{primaryCompany?.name || primaryContact?.companyName || "—"}</span>
                <span className="truncate font-medium">{primaryContact?.name || "—"}</span>
                <div className="flex items-center justify-end gap-1">
                  {primaryCompany ? (
                    <Button variant="ghost" size="icon" className="h-11 w-11 p-0 text-destructive hover:bg-destructive/10" aria-label="Rimuovi azienda principale" title="Rimuovi azienda principale" onClick={() => setAssociationDecision({ target: "deal_primary_company", associationId: dealId, label: `${primaryCompany.name} ↔ Deal` })}><Trash2 className="h-4 w-4" /></Button>
                  ) : null}
                  {primaryContact ? (
                    <Button variant="ghost" size="icon" className="h-11 w-11 p-0 text-destructive hover:bg-destructive/10" aria-label="Rimuovi contatto principale" title="Rimuovi contatto principale" onClick={() => setAssociationDecision({ target: "deal_primary_contact", associationId: dealId, label: `${primaryContact.name} ↔ Deal` })}><Trash2 className="h-4 w-4" /></Button>
                  ) : null}
                </div>
              </div>
            ) : null}
            {displayedParticipants.map((participant) => (
              <div key={participant.id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center border-b border-border px-3 py-2 text-sm last:border-b-0">
                <span className="truncate">{participant.entityType === "company" ? participant.entityName : participant.entityCompanyName || "—"}</span>
                <span className="truncate font-medium">{participant.entityType === "company" ? "—" : participant.entityName || `Record #${participant.entityId}`}</span>
                <Button variant="ghost" size="icon" className="h-11 w-11 p-0 text-destructive hover:bg-destructive/10" aria-label="Decidi associazione partecipante" onClick={() => setAssociationDecision({ target: "deal_participant", associationId: participant.id, label: participant.entityName || `Partecipante #${participant.entityId}` })}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl rounded-none">
          <DialogHeader>
            <DialogTitle>Aggiungi persona o azienda al Deal</DialogTitle>
            <DialogDescription>Collega un soggetto coinvolto. Questo non crea una Sinergia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 border border-border">
              {(["contact", "company", "branch"] as ParticipantType[]).map((type) => (
                <Button key={type} type="button" variant={entityType === type ? "default" : "ghost"} className="rounded-none" onClick={() => { setEntityType(type); setEntityId(""); setEntityLabel(""); setSuggestedContactIds([]); }}>
                  {type === "contact" ? "Persona" : type === "company" ? "Azienda" : "Filiale"}
                </Button>
              ))}
            </div>
            <CrmRecordAutocomplete
              entityType={entityType}
              value={entityId}
              onValueChange={(value) => { setEntityId(value); setSuggestedContactIds([]); }}
              onOptionSelect={(option) => setEntityLabel(option?.label || "")}
              selectedLabel={entityLabel}
              placeholder={`Cerca ${entityType === "contact" ? "persona" : entityType === "company" ? "azienda" : "filiale"}...`}
              searchPlaceholder="Cerca in tutto il CRM..."
              icon={typeIcon(entityType)}
              // Il contatto/azienda principale del deal e' gia' mostrato come
              // riga a se': ripescarlo qui creerebbe un doppione visibile
              // (bug riprodotto anche su dati reali, deal #342). Non basta
              // segnalarlo dopo il salvataggio: va tolto dalla ricerca prima
              // che l'utente possa selezionarlo.
              excludeIds={
                entityType === "contact" && primaryContact ? [primaryContact.id]
                  : entityType === "company" && primaryCompany ? [primaryCompany.id]
                  : []
              }
            />
            {entityId && entityType !== "contact" ? (
              <div className="border border-border bg-background">
                <div className="border-b border-border bg-muted/30 px-3 py-2">
                  <p className="text-sm font-semibold">Contatti suggeriti</p>
                  <p className="text-xs text-muted-foreground">Seleziona le persone da collegare insieme a {entityLabel || "questo soggetto"}.</p>
                </div>
                {isFetchingSuggestions ? (
                  <p className="px-3 py-4 text-sm text-muted-foreground">Caricamento contatti collegati…</p>
                ) : suggestedContactGroups?.groups.some((group) => group.contacts.length > 0) ? (
                  <div className="max-h-64 overflow-y-auto">
                    {suggestedContactGroups.groups.filter((group) => group.contacts.length > 0).map((group) => (
                      <div key={`${group.relationship}-${group.name || group.companyName}`} className="border-b border-border last:border-b-0">
                        <p className="bg-muted/20 px-3 py-1.5 text-xs font-semibold text-muted-foreground">
                          {group.relationship === "direct"
                            ? entityType === "branch" ? `Filiale · ${group.name}` : `Azienda · ${group.companyName}`
                            : group.relationship === "controlling_company" ? `Controllante · ${group.companyName}` : `Casa madre · ${group.name}`}
                        </p>
                        {group.contacts.map((contact) => {
                          const alreadyLinked = linkedContactIds.has(contact.id);
                          const checked = suggestedContactIds.includes(contact.id) || alreadyLinked;
                          return (
                            <label key={contact.id} className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-muted/20">
                              <Checkbox
                                checked={checked}
                                disabled={alreadyLinked}
                                onCheckedChange={(next) => setSuggestedContactIds((current) => next
                                  ? [...current, contact.id]
                                  : current.filter((id) => id !== contact.id))}
                                className="mt-0.5 rounded-none"
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium">{`${contact.firstName || ""} ${contact.lastName || ""}`.trim() || `Contatto #${contact.id}`}</span>
                                <span className="block truncate text-xs text-muted-foreground">{[contact.role, contact.companyEmail, alreadyLinked ? "Già nel Deal" : null].filter(Boolean).join(" · ")}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="px-3 py-4 text-sm text-muted-foreground">Nessun contatto collegato disponibile.</p>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button disabled={!entityId || addParticipant.isPending} onClick={() => addParticipant.mutate()}>
              {suggestedContactIds.length > 0 ? `Collega soggetto + ${suggestedContactIds.length} contatti` : "Collega al Deal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AssociationDecisionDialog
        open={Boolean(associationDecision)}
        onOpenChange={(open) => { if (!open) setAssociationDecision(null); }}
        target={associationDecision?.target ?? "deal_participant"}
        associationId={associationDecision?.associationId ?? null}
        label={associationDecision?.label ?? ""}
        onApplied={() => {
          setAssociationDecision(null);
          invalidateDealViews(queryClient, dealId, ["participants"]);
        }}
      />
    </>
  );
}
